import { FunctionComponent, useState } from "react";

import { CompanyShowBucket } from "@signalist/shared-types";

import { CompanyRowsPage } from "@/pages/Private/redux/list/list-companies.api";

import {
	companyBucketCount,
	COMPANY_SHOW_BUCKET_LABEL,
	COMPANY_SHOW_BUCKETS,
	totalCompanyCount,
} from "./companyBuckets";

interface CompanyShowFilterProps {
	/** The selected bucket, or undefined for "All companies". */
	value?: CompanyShowBucket;
	buckets?: CompanyRowsPage["buckets"];
	onChange: (bucket?: CompanyShowBucket) => void;
}

/**
 * The Companies-tab Show filter (PREC-4400): a single-select dropdown of the six company buckets,
 * each with its own total, plus "All companies". Its own entry for "No person searched yet", apart
 * from "No person found" ("we searched and found nobody"). Closes on an outside click through a
 * transparent backdrop, so there is no document-level listener to retarget in a shadow root.
 */
export const CompanyShowFilter: FunctionComponent<CompanyShowFilterProps> = ({
	value,
	buckets,
	onChange,
}) => {
	const [open, setOpen] = useState(false);

	const label = value ? COMPANY_SHOW_BUCKET_LABEL[value] : "All companies";

	const pick = (bucket?: CompanyShowBucket) => {
		onChange(bucket);
		setOpen(false);
	};

	return (
		<div className="relative">
			<button
				className="flex items-center gap-1 rounded border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
				type="button"
				onClick={() => setOpen((previous) => !previous)}
			>
				<span className="text-gray-500">Show:</span>
				<span className="font-medium">{label}</span>
			</button>

			{open ? (
				<>
					<button
						aria-label="Close filter"
						className="fixed inset-0 z-10 cursor-default"
						type="button"
						onClick={() => setOpen(false)}
					/>
					<div className="absolute left-0 z-20 mt-1 w-64 rounded border border-gray-200 bg-white py-1 shadow-lg">
						<button
							className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${
								value ? "" : "font-semibold"
							}`}
							type="button"
							onClick={() => pick(undefined)}
						>
							<span>All companies</span>
							<span className="text-gray-400">{totalCompanyCount(buckets)}</span>
						</button>
						{COMPANY_SHOW_BUCKETS.map((bucket) => (
							<button
								key={bucket}
								className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${
									value === bucket ? "font-semibold" : ""
								}`}
								type="button"
								onClick={() => pick(bucket)}
							>
								<span>{COMPANY_SHOW_BUCKET_LABEL[bucket]}</span>
								<span className="text-gray-400">{companyBucketCount(buckets, bucket)}</span>
							</button>
						))}
					</div>
				</>
			) : null}
		</div>
	);
};
